import _ from "lodash"

import { CategoryId, DealId, SkuId } from "shared/hubrise/ids"
import { ICatalog, ICategory, IDeal } from "shared/hubrise/models"
import * as DealLine from "shared/hubrise/utils/DealLine"
import {
  findById,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  replaceInArrayById,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as Restrictions from "./Restrictions"
import { generateRefsForItems, GenerateRefsOptions, removeAllRefsFromItems } from "./ref"

/**
 * Add a deal.
 */
export function add(
  catalog: ICatalog,
  insertAfter: IDeal | undefined,
  values: Partial<IDeal>,
): { newCatalog: ICatalog; deal: IDeal } {
  const position: number = insertAfter ? catalog.data.deals.findIndex((deal) => deal.id === insertAfter.id) + 1 : 0
  const id = DealId(catalog.idGenerator())
  const defaultValues: IDeal = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    ref: "",
    categoryId: null,
    name: "",
    description: "",
    restrictions: { enabled: true },
    couponCodes: [],
    tags: [],
    imageHrIds: [],
    lines: [DealLine.create(catalog)],
  }
  const deal: IDeal = { ...defaultValues, ...values }
  const deals: Array<IDeal> = insertItemInArray(catalog.data.deals, deal, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { deals })
  return { newCatalog, deal }
}

/**
 * Return the deal matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: DealId): IDeal => {
  const deal = findById(catalog.data.deals, id)
  if (!deal) throw `Deal ${id} not found`
  return deal
}

/**
 * Returns true if the deal should be visible in <Preview>.
 * @param deal
 * @param variantRef
 */
export function isVisibleInPreview(deal: IDeal, variantRef: string | null): boolean {
  return Restrictions.isVisibleInPreview(deal.restrictions, variantRef)
}

/**
 * Return a "fake" category for the deals that are not in any category.
 * @param catalog
 * @param name The name of the category.
 */
export const fakeDealCategory = (catalog: ICatalog, name: string): ICategory | undefined => {
  const orphanDeals = inCategory(catalog, null)
  if (orphanDeals.length > 0) {
    return {
      id: fakeDealCategoryId,
      name,
      description: "",
      imageHrIds: [],
      tags: [],
      parentId: null,
    }
  }
}

const fakeDealCategoryId = CategoryId(-1000)

/**
 * Return a new array of deals after removing a set of skuIds. Deals with one or more empty lines are removed from the
 * result.
 * @param catalog
 * @param skuIds
 */
export function deleteSkuIds(catalog: ICatalog, skuIds: Array<SkuId>): Array<IDeal> {
  return _.compact(
    catalog.data.deals.map((deal) => {
      const lines = deal.lines.map((line) => ({
        ...line,
        skus: line.skus.filter(({ skuId }) => !skuIds.includes(skuId)),
      }))
      return lines.some((line) => line.skus.length === 0) ? null : { ...deal, lines }
    }),
  )
}

/**
 * Detach the deal from its category, if the category belongs to the list.
 * @param deal
 * @param categoryIds
 */
export function detachFromCategory(deal: IDeal, categoryIds: Array<CategoryId>): IDeal {
  if (deal.categoryId === null || !categoryIds.includes(deal.categoryId)) {
    return deal
  } else {
    return { ...deal, categoryId: null }
  }
}

/**
 * Delete the deal. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param id
 */
export function doDelete(catalog: ICatalog, id: DealId): ICatalog {
  return updatedCatalog(catalog, { deals: removeInArrayById(catalog.data.deals, id) })
}

/**
 * Return the list of deals belonging to the category.
 * @param catalog
 * @param category
 */
export const inCategory = (catalog: ICatalog, category: ICategory | null): Array<IDeal> =>
  catalog.data.deals.filter((deal) => deal.categoryId === (category === null ? null : category.id))

/**
 * Returns true if the category is the "fake" deal category.
 * @param category
 */
export const isFakeDealCategory = (category: ICategory): boolean => category.id === fakeDealCategoryId

/**
 * Move a deal to a new position.
 * @param catalog
 * @param deal
 * @param newPosition
 */
export function move(catalog: ICatalog, deal: IDeal, newPosition: number): ICatalog {
  const deals: Array<IDeal> = moveItemWithinArray(catalog.data.deals, deal, newPosition)
  return updatedCatalog(catalog, { deals })
}

/**
 * Update a deal and return the resulting catalog.
 * @param catalog
 * @param id
 * @param fieldName
 * @param value
 */
export function update<T extends keyof IDeal>(catalog: ICatalog, id: DealId, fieldName: T, value: IDeal[T]): ICatalog {
  return updatedCatalog(catalog, {
    deals: replaceInArrayById(catalog.data.deals, id, (deal) => ({
      ...deal,
      [fieldName]: value,
    })),
  })
}

/**
 * Remove refs from all deals.
 * @param catalog
 */
export function removeAllRefs(catalog: ICatalog): ICatalog {
  return updatedCatalog(catalog, {
    deals: removeAllRefsFromItems(catalog.data.deals),
  })
}

/**
 * Generate refs for all deals.
 * @param catalog
 * @param options
 */
export function generateRefs(catalog: ICatalog, options: GenerateRefsOptions): ICatalog {
  return updatedCatalog(catalog, {
    deals: generateRefsForItems(catalog.data.deals, options, "deal"),
  })
}

/**
 * Enable or disable the deal. An optional variant ref can be provided.
 * @param catalog
 * @param dealId
 * @param enabled
 * @param variantRef
 */
export function enable(catalog: ICatalog, dealId: DealId, enabled: boolean, variantRef?: string): ICatalog {
  return updatedCatalog(catalog, {
    deals: replaceInArrayById(catalog.data.deals, dealId, (deal) => ({
      ...deal,
      restrictions:
        variantRef === undefined
          ? Restrictions.enable(deal.restrictions, enabled)
          : Restrictions.enableVariant(catalog.data.variants, deal.restrictions, variantRef, enabled),
    })),
  })
}

/**
 * Enable or disable all deals. An optional variant ref can be provided.
 * @param catalog
 * @param enabled
 * @param variantRef
 */
export function enableAll(catalog: ICatalog, enabled: boolean, variantRef?: string): ICatalog {
  return updatedCatalog(catalog, {
    deals: catalog.data.deals.map((deal) => ({
      ...deal,
      restrictions:
        variantRef === undefined
          ? Restrictions.enable(deal.restrictions, enabled)
          : Restrictions.enableVariant(catalog.data.variants, deal.restrictions, variantRef, enabled),
    })),
  })
}
